import { Injectable, NgZone, OnDestroy } from "@angular/core";
import { environment, HelperService, StorageService } from "@mypxplat/xplat/core";

export const MYFENDER_EVENTS_PATH = "data/myfender-events";
export const MAX_BATCH_MESSAGES = 10;
export const MAX_BODY_BYTES = 65536;
export const MAX_MESSAGE_BYTES = 16384;
export const FLUSH_DELAY_MS = 2000;
export const ANONYMOUS_ID_KEY = "myfender_events_anonymous_id";

/** Where analytics.js keeps its anonymous id; reused once so DMP and Segment share a visitor id. */
const SEGMENT_ANONYMOUS_ID_KEY = "ajs_anonymous_id";
const BODY_PREFIX = '{"batch":[';
const BODY_SUFFIX = "]}";

/**
 * Sends what MyFender tracks to Datamaster Pro (DENG-1098).
 *
 * Segment is being decommissioned. While it is, web-fender dual-writes: every event still goes
 * to Segment through AvoService, and is also posted here to paeapi `POST /data/myfender-events`,
 * which forwards it to DMP (EventBridge → Databricks) as `source = myfender_events`. Event
 * sources call track/page/identify; today that's AvoService's destination, and it will be a
 * typed tracking-plan module once Avo is removed.
 *
 * The wire format is ingest contract v2 (paeapi `docs/MYFENDER-EVENTS-DMP.md`, "Ingest
 * endpoint", paeapi#1236): a `{"batch":[…]}` body of 1–10 Segment-shaped messages and at most
 * 64 KB, optionally with the Fender Cognito ID token in `X-USER-TOKEN`. The server sets userId
 * and fcId from that token, so messages never carry them.
 *
 * It builds Segment's message shape itself (anonymousId, messageId, timestamp, context) rather
 * than capturing analytics.js's, because it must work with Avo and Segment both off.
 *
 * It uses native `fetch` with `keepalive` and `navigator.sendBeacon`, not HttpClient: our
 * HttpClient is `withXhr()`, so it has no keepalive and batches would die on navigation, and
 * web-fender's AuthInterceptor would add a token refresh and logout-on-401 to a fire-and-forget
 * call. Nothing is retried and no response is read.
 *
 * Gated by `environment.features.myfenderEventsDmp` (off in prod). When Osano is on the page
 * (prod only), a batch is sent only if the visitor accepted ANALYTICS (`Osano.cm.analytics`);
 * where Osano is expected but hasn't loaded yet, the batch is dropped.
 */
@Injectable({ providedIn: "root" })
export class MyfenderEventsService implements OnDestroy {
  private _queue: string[] = [];
  private _queueBytes = 0;
  private _timer: ReturnType<typeof setTimeout> | null = null;
  private _listening = false;
  private _anonymousId: string | null = null;

  constructor(
    private _helperService: HelperService,
    private _storageService: StorageService,
    private _zone: NgZone
  ) {}

  get enabled(): boolean {
    return environment.features.myfenderEventsDmp === true;
  }

  track(event: string, properties: Record<string, any>): void {
    this._enqueue(() => ({ type: "track", event, properties: properties ?? {}, ...this._envelope() }));
  }

  /** Page properties follow analytics-next's addPageContext: page context, then the given properties, then name. */
  page(name: string, properties: Record<string, any>): void {
    this._enqueue(() => ({ type: "page", name, properties: { ...pageContext(), ...properties, name }, ...this._envelope() }));
  }

  identify(traits: Record<string, any>): void {
    this._enqueue(() => ({ type: "identify", traits: traits ?? {}, ...this._envelope() }));
  }

  ngOnDestroy(): void {
    this._clearTimer();
    if (this._listening) {
      window.removeEventListener("pagehide", this._onPageHide);
      document.removeEventListener("visibilitychange", this._onVisibilityChange);
      this._listening = false;
    }
  }

  private _onPageHide = (): void => this._flush(true);

  private _onVisibilityChange = (): void => {
    if (document.visibilityState === "hidden") this._flush(false);
  };

  /** Serializes now, so later changes to the caller's objects don't leak in, and never throws. */
  private _enqueue(buildMessage: () => Record<string, any>): void {
    if (!this.enabled) return;
    try {
      const json = JSON.stringify(buildMessage());
      const size = utf8Bytes(json);
      if (size > MAX_MESSAGE_BYTES) return;
      this._listen();
      // Each queued item after the first adds a comma.
      const bodyBytes = BODY_PREFIX.length + BODY_SUFFIX.length + this._queueBytes + this._queue.length + size;
      if (this._queue.length > 0 && bodyBytes > MAX_BODY_BYTES) this._flush(false);
      this._queue.push(json);
      this._queueBytes += size;
      if (this._queue.length >= MAX_BATCH_MESSAGES) {
        this._flush(false);
      } else if (this._timer === null) {
        this._zone.runOutsideAngular(() => {
          this._timer = setTimeout(() => this._flush(false), FLUSH_DELAY_MS);
        });
      }
    } catch {
      // Unserializable (e.g. circular) or no crypto: drop the message.
    }
  }

  private _envelope(): Record<string, any> {
    return {
      anonymousId: this._getAnonymousId(),
      messageId: uuid(),
      timestamp: new Date().toISOString(),
      context: buildContext(),
    };
  }

  private _getAnonymousId(): string {
    if (this._anonymousId) return this._anonymousId;
    let id = this._storageService.getItem(ANONYMOUS_ID_KEY);
    if (!isUsableId(id)) {
      const segmentId = this._storageService.getItem(SEGMENT_ANONYMOUS_ID_KEY);
      id = isUsableId(segmentId) ? segmentId : uuid();
      this._storageService.setItem(ANONYMOUS_ID_KEY, id);
    }
    this._anonymousId = id;
    return id;
  }

  private _listen(): void {
    if (this._listening) return;
    this._listening = true;
    this._zone.runOutsideAngular(() => {
      window.addEventListener("pagehide", this._onPageHide);
      document.addEventListener("visibilitychange", this._onVisibilityChange);
    });
  }

  private _clearTimer(): void {
    if (this._timer !== null) {
      clearTimeout(this._timer);
      this._timer = null;
    }
  }

  /** `leaving` (pagehide) always takes the beacon path: a keepalive fetch may not survive unload. */
  private _flush(leaving: boolean): void {
    this._clearTimer();
    if (this._queue.length === 0) return;
    const body = BODY_PREFIX + this._queue.join(",") + BODY_SUFFIX;
    this._queue = [];
    this._queueBytes = 0;
    this._zone.runOutsideAngular(() => {
      try {
        if (hasAnalyticsConsent()) this._send(body, leaving);
      } catch {
        // Fire-and-forget: a batch that can't be sent is dropped.
      }
    });
  }

  private _send(body: string, leaving: boolean): void {
    const url = `${environment.paeApiUrl}${MYFENDER_EVENTS_PATH}`;
    const idToken = leaving ? null : this._signedInIdToken();
    if (idToken) {
      post(url, body, { "Content-Type": "application/json", "X-USER-TOKEN": idToken });
    } else if (typeof navigator.sendBeacon === "function") {
      navigator.sendBeacon(url, body);
    } else {
      post(url, body, { "Content-Type": "text/plain" });
    }
  }

  private _signedInIdToken(): string | null {
    const idToken = this._helperService.idToken;
    if (typeof idToken !== "string" || idToken === "") return null;
    return Number(this._helperService.tokenExpiry) * 1000 > Date.now() ? idToken : null;
  }
}

function post(url: string, body: string, headers: Record<string, string>): void {
  fetch(url, { method: "POST", keepalive: true, credentials: "omit", headers, body }).catch(() => undefined);
}

/** Where Osano is on the page, send only with ANALYTICS accepted. */
function hasAnalyticsConsent(): boolean {
  const cm = (window as any).Osano?.cm;
  if (cm && typeof cm === "object") return cm.analytics === true;
  // Osano is loaded only in prod with cookie management on (apps/web-fender/src/app/app.component.ts:222);
  // until it's on the page, consent is unknown there, so the batch is dropped.
  return !(environment.features.cookie_management && environment.stage === "prod");
}

function isUsableId(id: unknown): id is string {
  return typeof id === "string" && id.length >= 1 && id.length <= 100;
}

function utf8Bytes(text: string): number {
  return new TextEncoder().encode(text).length;
}

/** RFC 4122 v4, from getRandomValues where randomUUID isn't available (older Safari, non-secure contexts). */
function uuid(): string {
  if (typeof crypto.randomUUID === "function") return crypto.randomUUID();
  const b = crypto.getRandomValues(new Uint8Array(16));
  b[6] = (b[6] & 0x0f) | 0x40;
  b[8] = (b[8] & 0x3f) | 0x80;
  const hex = Array.from(b, (byte) => byte.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

function pageContext(): Record<string, string> {
  return {
    path: location.pathname,
    referrer: document.referrer,
    search: location.search,
    title: document.title,
    url: location.href,
  };
}

function buildContext(): Record<string, any> {
  const context: Record<string, any> = {
    page: pageContext(),
    userAgent: navigator.userAgent,
    locale: navigator.language,
  };
  const timezone = resolvedTimezone();
  if (timezone) context.timezone = timezone;
  context.library = { name: "myfender-events", version: "1" };
  const campaign = utmCampaign();
  if (campaign) context.campaign = campaign;
  return context;
}

function resolvedTimezone(): string | undefined {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return undefined;
  }
}

/** Like analytics.js: each `utm_x` becomes `x`, and `utm_campaign` becomes `name`. */
function utmCampaign(): Record<string, string> | null {
  const campaign: Record<string, string> = {};
  new URLSearchParams(location.search).forEach((value, key) => {
    if (!key.startsWith("utm_") || key.length === 4) return;
    const field = key.slice(4);
    campaign[field === "campaign" ? "name" : field] = value;
  });
  return Object.keys(campaign).length > 0 ? campaign : null;
}
